// src/views/Dashboard.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Split from 'react-split';
import './Dashboard.css';
import './UnshippedOrders.css';
import './UnpaidInvoices.css';
import { showToast, greetUserToast } from '../utils/toast';
import { hasAnyRight, INVOICE_RIGHTS } from '../utils/moduleRights.js';

const API_BASE = (() => {
  try {
    const raw = (import.meta.env.VITE_API_URL || '').trim();
    if (!raw) return '';
    if (typeof window !== 'undefined') {
      const u = new URL(raw, window.location.origin);
      if (window.location.protocol === 'https:' && u.protocol === 'http:') return '';
      return u.origin;
    }
    return raw;
  } catch {
    return '';
  }
})();

const DEFAULT_LOW_STOCK_THRESHOLD = (() => {
  const raw = Number(import.meta.env.VITE_LOW_STOCK_THRESHOLD);
  return Number.isFinite(raw) && raw >= 0 ? raw : 5;
})();

const NO_IMAGE_PLACEHOLDER = '/no-image-placeholder.svg';

/* ---------- user/role helpers ---------- */
function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}') || {};
  } catch {
    return {};
  }
}
function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function getUserClientId(u) {
  const cid = u.clientid ?? u.clientId ?? u.client_id;
  return Number.isFinite(+cid) && +cid > 0 ? String(cid) : '';
}

function getAllowedClientIdsFromUser(u) {
  if (!u || !Array.isArray(u.allowedClientIds)) return [];
  const normalized = u.allowedClientIds
    .map((cid) => Number(cid))
    .filter((cid) => Number.isFinite(cid) && cid > 0);
  return Array.from(new Set(normalized)).map((cid) => String(cid));
}

function formatLowThreshold(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString() : 'N/A';
}

function formatCurrency(value) {
  const num = Number(value);
  return Number.isFinite(num)
    ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0.00';
}

const BASE64_RE = /^[A-Za-z0-9+/=_-]+$/;
function normalizeImageValue(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return '';
    if (/^data:image\//i.test(s)) return s;
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith('//')) {
      try {
        if (typeof window !== 'undefined' && window?.location?.protocol) {
          return `${window.location.protocol}${s}`;
        }
      } catch {}
      return `https:${s}`;
    }
    if (s.startsWith('/')) return s;
    if (/^[\w./-]+\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(s)) {
      return s.startsWith('/') ? s : `/${s}`;
    }
    const compact = s.replace(/\s+/g, '');
    if (compact.length > 32 && BASE64_RE.test(compact)) {
      return `data:image/jpeg;base64,${compact}`;
    }
    return '';
  }
  if (typeof window !== 'undefined') {
    try {
      const raw = Array.isArray(value?.data)
        ? value.data
        : Array.isArray(value)
          ? value
          : value instanceof ArrayBuffer
            ? Array.from(new Uint8Array(value))
            : value?.type === 'Buffer' && Array.isArray(value?.data)
              ? value.data
              : null;
      if (raw && raw.length && typeof Blob !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
        const blob = new Blob([new Uint8Array(raw)], { type: 'image/jpeg' });
        return URL.createObjectURL(blob);
      }
    } catch {}
  }
  return '';
}

function getItemImageSrc(item) {
  const fields = ['imageUrl', 'thumbnail', 'thumb', 'image1'];
  for (const key of fields) {
    const src = normalizeImageValue(item?.[key]);
    if (src) return src;
  }
  return '';
}

const PRE_SHIPPED_STATUS_NAMES = [
  'NEW',
  'REQUESTED',
  'PICKING',
  'PICKED',
  'PACKING',
  'READY TO SHIP',
  'BACK ORDER'
];
const PRE_SHIPPED_STATUS_SET = new Set(PRE_SHIPPED_STATUS_NAMES);

const normalizeStatusName = (name) => String(name || '').trim().toUpperCase();
const isStatusBelowShipped = (name) => PRE_SHIPPED_STATUS_SET.has(normalizeStatusName(name));

function splitDateTime(value) {
  if (!value) return null;
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return {
      date: dt.toLocaleDateString(),
      time: dt.toLocaleTimeString()
    };
  } catch {
    return null;
  }
}
/* -------------------------------------- */

const DASHBOARD_CARD_KEYS = ['lowStock', 'unshippedOrders', 'unshippedEOrders', 'unpaidInvoices', 'revenue'];
const DASHBOARD_PREF_KEY = 'dashboard.visible.cards';
const DASHBOARD_ORDER_KEY = 'dashboard.card.order';
const DEFAULT_CARD_ORDER = [...DASHBOARD_CARD_KEYS];

const sanitizeCardOrder = (order) => {
  const filtered = Array.isArray(order)
    ? order.filter((key, idx) => DASHBOARD_CARD_KEYS.includes(key) && order.indexOf(key) === idx)
    : [];
  return [...filtered, ...DEFAULT_CARD_ORDER.filter((key) => !filtered.includes(key))];
};

const Dashboard = ({ showOrdersEnabled = true, showInvoicesEnabled = true }) => {
  const user = useMemo(() => getCurrentUser(), []);
  const IS_CLIENT = useMemo(() => isClientUser(user), [user]);
  const LOCKED_CLIENT_ID = useMemo(() => getUserClientId(user), [user]);
  const allowedClientIds = useMemo(() => getAllowedClientIdsFromUser(user), [user]);
  const canSeeUnpaidInvoices = useMemo(
    () => showInvoicesEnabled && hasAnyRight(user, INVOICE_RIGHTS),
    [showInvoicesEnabled, user]
  );
  const lowStockClientScopeId = useMemo(() => {
    if (!IS_CLIENT) return '';
    if (allowedClientIds.length === 1) return allowedClientIds[0];
    if (!allowedClientIds.length && LOCKED_CLIENT_ID) return LOCKED_CLIENT_ID;
    return '';
  }, [IS_CLIENT, allowedClientIds, LOCKED_CLIENT_ID]);

  const [visibleCards, setVisibleCards] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(DASHBOARD_PREF_KEY) || '{}');
      if (stored && typeof stored === 'object') {
        return {
          lowStock: stored.lowStock !== false,
          unshippedOrders: stored.unshippedOrders !== false,
          unshippedEOrders: stored.unshippedEOrders !== false,
          unpaidInvoices: stored.unpaidInvoices !== false,
          revenue: stored.revenue !== false,
        };
      }
    } catch {}
    return {
      lowStock: true,
      unshippedOrders: true,
      unshippedEOrders: true,
      unpaidInvoices: true,
      revenue: true,
    };
  });
  const [showConfig, setShowConfig] = useState(false);
  const toggleCardVisibility = (key) => {
    setVisibleCards((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(DASHBOARD_PREF_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(DASHBOARD_ORDER_KEY) || '[]');
      return sanitizeCardOrder(raw);
    } catch {
      return DEFAULT_CARD_ORDER;
    }
  });
  const [draggingCard, setDraggingCard] = useState(null);
  const [splitVersion, setSplitVersion] = useState(0);

  const persistCardOrder = (order) => {
    try {
      localStorage.setItem(DASHBOARD_ORDER_KEY, JSON.stringify(order));
    } catch {}
  };

  const reorderCards = useCallback(
    (fromKey, toKey) => {
      if (!fromKey || !toKey || fromKey === toKey) return;
      setCardOrder((prev) => {
        const base = sanitizeCardOrder(prev);
        const fromIdx = base.indexOf(fromKey);
        const toIdx = base.indexOf(toKey);
        if (fromIdx === -1 || toIdx === -1) return base;
        const updated = [...base];
        const [moved] = updated.splice(fromIdx, 1);
        updated.splice(toIdx, 0, moved);
        persistCardOrder(updated);
        return updated;
      });
    },
    []
  );

  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  const [sortFieldOrders, setSortFieldOrders] = useState('orderNumber');
  const [sortOrderOrders, setSortOrderOrders] = useState('ASC');

  const [sortFieldInvoices, setSortFieldInvoices] = useState('invoiceId');
  const [sortOrderInvoices, setSortOrderInvoices] = useState('ASC');

  const [newEOrders, setNewEOrders] = useState([]);
  const [loadingNewEOrders, setLoadingNewEOrders] = useState(true);
  const [sortFieldEOrders, setSortFieldEOrders] = useState('orderdate');
  const [sortOrderEOrders, setSortOrderEOrders] = useState('DESC');
  const [preShippedStatusIds, setPreShippedStatusIds] = useState(null);

  const [revenue, setRevenue] = useState([]);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [lowStock, setLowStock] = useState([]);
  const [loadingLowStock, setLoadingLowStock] = useState(true);
  const fallbackLowStockThreshold = DEFAULT_LOW_STOCK_THRESHOLD;

  const monthLabel = (d) =>
    d.toLocaleString(undefined, { month: 'short', year: 'numeric' });

  // Fetch revenue (internal users only)
  useEffect(() => {
    if (IS_CLIENT || !showInvoicesEnabled) {
      // Clients (and when invoices are hidden) do not see revenue chart
      setRevenue([]);
      setLoadingRevenue(false);
      return;
    }

    const fetchRevenue = async () => {
      setLoadingRevenue(true);
      try {
        const res = await fetch(`${API_BASE}/api/reports/revenue/monthly?months=6`);
        const data = await res.json(); // [{ monthStart: "YYYY-MM-01", revenue: number }, ...]
        const map = new Map(
          (Array.isArray(data) ? data : []).map((r) => [
            r.monthStart,
            Number(r.revenue) || 0
          ])
        );

        const now = new Date();
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            '0'
          )}-01`;
          months.push({
            key,
            label: monthLabel(d),
            total: map.get(key) ?? 0
          });
        }
        setRevenue(months);
      } catch (e) {
        console.error('Failed to fetch monthly revenue:', e);
        setRevenue([]);
      } finally {
        setLoadingRevenue(false);
      }
    };
    fetchRevenue();
  }, [IS_CLIENT, showInvoicesEnabled]);

  // Low-inventory SKUs
  useEffect(() => {
    const fetchLowStock = async () => {
      setLoadingLowStock(true);
      try {
        const params = new URLSearchParams({
          threshold: String(fallbackLowStockThreshold)
        });
        if (lowStockClientScopeId) {
          params.set('clientid', lowStockClientScopeId);
        }
        const res = await fetch(`${API_BASE}/api/inventory/low-stock?${params.toString()}`);
        const data = await res.json();
        let items = [];
        if (Array.isArray(data?.items)) items = data.items;
        else if (Array.isArray(data)) items = data;

        const filtered = items.filter((item) => Number(item?.lowThreshold) !== -1);
        setLowStock(filtered);
      } catch (e) {
        console.error('Failed to fetch low-stock inventory:', e);
        setLowStock([]);
      } finally {
        setLoadingLowStock(false);
      }
    };
    fetchLowStock();
  }, [fallbackLowStockThreshold, lowStockClientScopeId]);

  // Unshipped orders (filtered for clients)
  useEffect(() => {
    if (!showOrdersEnabled) {
      setOrders([]);
      setLoadingOrders(false);
      return;
    }
    const fetchOrders = async () => {
      setLoadingOrders(true);
      try {
        const params = new URLSearchParams({
          sortField: sortFieldOrders,
          sortOrder: sortOrderOrders
        });
        if (IS_CLIENT && LOCKED_CLIENT_ID) {
          params.set('clientid', LOCKED_CLIENT_ID);
        }
        const response = await fetch(`${API_BASE}/api/unshipped_orders?${params.toString()}`);
        const data = await response.json();
        setOrders(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch unshipped orders:', error);
        setOrders([]);
      } finally {
        setLoadingOrders(false);
      }
    };
    fetchOrders();
  }, [showOrdersEnabled, sortFieldOrders, sortOrderOrders, IS_CLIENT, LOCKED_CLIENT_ID]);

  // Unpaid invoices (filtered for clients)
  useEffect(() => {
    if (!canSeeUnpaidInvoices) {
      setInvoices([]);
      setLoadingInvoices(false);
      return;
    }
    const fetchInvoices = async () => {
      setLoadingInvoices(true);
      try {
        const params = new URLSearchParams({
          sortField: sortFieldInvoices,
          sortOrder: sortOrderInvoices
        });
        if (IS_CLIENT && LOCKED_CLIENT_ID) {
          params.set('clientid', LOCKED_CLIENT_ID);
        }
        const response = await fetch(`${API_BASE}/api/unpaid_invoices?${params.toString()}`);
        const data = await response.json();
        setInvoices(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch unpaid invoices:', error);
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, [canSeeUnpaidInvoices, sortFieldInvoices, sortOrderInvoices, IS_CLIENT, LOCKED_CLIENT_ID]);

  useEffect(() => {
    let cancelled = false;
    const fetchStatusIds = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/eorders/statuses`);
        const data = await response.json();
        if (cancelled) return;
        const ids = (Array.isArray(data) ? data : [])
          .filter((row) => isStatusBelowShipped(row?.name))
          .map((row) => Number(row?.id))
          .filter((n) => Number.isFinite(n));
        setPreShippedStatusIds(ids);
      } catch (error) {
        console.error('Failed to load e-order statuses:', error);
        if (!cancelled) setPreShippedStatusIds([]);
      }
    };
    fetchStatusIds();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!showOrdersEnabled) {
      setNewEOrders([]);
      setLoadingNewEOrders(false);
      return;
    }
    if (preShippedStatusIds === null) {
      setLoadingNewEOrders(true);
      return;
    }
    let cancelled = false;
    const fetchNewEOrders = async () => {
      setLoadingNewEOrders(true);
      try {
        const params = new URLSearchParams({
          limit: '200',
          sortField: sortFieldEOrders,
          sortOrder: sortOrderEOrders,
          deleted: '0'
        });
        if (preShippedStatusIds.length > 0) {
          params.set('our_order_status_ids', preShippedStatusIds.join(','));
        }
        if (IS_CLIENT && LOCKED_CLIENT_ID) {
          params.set('clientid', LOCKED_CLIENT_ID);
        }
        const response = await fetch(`${API_BASE}/api/eorders?${params.toString()}`);
        const data = await response.json();
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const filteredRows = rows.filter((row) => isStatusBelowShipped(row?.ourstatusname));
        if (!cancelled) setNewEOrders(filteredRows);
      } catch (error) {
        console.error('Failed to fetch NEW e-orders:', error);
        if (!cancelled) setNewEOrders([]);
      } finally {
        if (!cancelled) setLoadingNewEOrders(false);
      }
    };
    fetchNewEOrders();
    return () => { cancelled = true; };
  }, [showOrdersEnabled, sortFieldEOrders, sortOrderEOrders, IS_CLIENT, LOCKED_CLIENT_ID, preShippedStatusIds]);

  const handleSortOrders = (field) => {
    if (sortFieldOrders === field) {
      setSortOrderOrders(sortOrderOrders === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortFieldOrders(field);
      setSortOrderOrders('ASC');
    }
  };

  const handleSortInvoices = (field) => {
    if (sortFieldInvoices === field) {
      setSortOrderInvoices(sortOrderInvoices === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortFieldInvoices(field);
      setSortOrderInvoices('ASC');
    }
  };

  const handleSortEOrders = (field) => {
    if (sortFieldEOrders === field) {
      setSortOrderEOrders(sortOrderEOrders === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortFieldEOrders(field);
      setSortOrderEOrders('ASC');
    }
  };

  const renderSortHeader = (label, field, currentField, currentOrder, onClick, extraClass = '') => {
    const cls = [
      currentField === field ? 'active-sort' : '',
      extraClass
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <th onClick={() => onClick(field)} className={cls}>
        {label} {currentField === field ? (currentOrder === 'ASC' ? '▲' : '▼') : ''}
      </th>
    );
  };

  const barColors = [
    '#ff6200', // orange
    '#0088FE', // blue
    '#00C49F', // teal
    '#FFBB28', // gold
    '#FF8042', // orange-red
    '#A28EFF', // purple
  ];

  const revenueMax = useMemo(
    () => Math.max(0, ...revenue.map((r) => Number(r.total) || 0)),
    [revenue]
  );

  // One-time greeting after successful login
  useEffect(() => {
    try {
      const flag = sessionStorage.getItem('justLoggedIn');
      if (flag === '1') {
        greetUserToast(user);
        sessionStorage.removeItem('justLoggedIn');
      }
    } catch {}
  }, [user]);

  const canRenderCard = useCallback(
    (key) => {
      switch (key) {
        case 'lowStock':
          return visibleCards.lowStock;
        case 'unshippedOrders':
          return showOrdersEnabled && visibleCards.unshippedOrders;
        case 'unshippedEOrders':
          return showOrdersEnabled && visibleCards.unshippedEOrders;
        case 'unpaidInvoices':
          return canSeeUnpaidInvoices && visibleCards.unpaidInvoices;
        case 'revenue':
          return !IS_CLIENT && showInvoicesEnabled && visibleCards.revenue;
        default:
          return false;
      }
    },
    [IS_CLIENT, canSeeUnpaidInvoices, showInvoicesEnabled, showOrdersEnabled, visibleCards]
  );

  const cardRefs = useRef({});
  const wrapperRefs = useRef({});
  const registerCardRef = useCallback((key, node) => {
    if (node) cardRefs.current[key] = node;
    else delete cardRefs.current[key];
  }, []);
  const rowRefs = useRef({});
  const registerRowRef = useCallback((key, node) => {
    if (node) rowRefs.current[key] = node;
    else delete rowRefs.current[key];
  }, []);
  const registerWrapperRef = useCallback((key, node) => {
    if (node) wrapperRefs.current[key] = node;
    else delete wrapperRefs.current[key];
  }, []);

  const splitElementStyle = useCallback((dimension, size, gutterSize) => {
    const safeGutter = Number.isFinite(Number(gutterSize)) ? Number(gutterSize) : 0;
    if (typeof size === 'string' && size.trim()) {
      return { [dimension]: size };
    }
    const safeSize = Number(size);
    return {
      [dimension]: Number.isFinite(safeSize)
        ? `calc(${safeSize}% - ${safeGutter}px)`
        : `calc(100% - ${safeGutter}px)`,
    };
  }, []);

  const splitGutterStyle = useCallback((dimension, gutterSize) => {
    const safeGutter = Number.isFinite(Number(gutterSize)) ? Number(gutterSize) : 10;
    return { [dimension]: `${safeGutter}px` };
  }, []);

  const arrangedRows = useMemo(() => {
    const rows = [];
    let currentRow = [];
    cardOrder.forEach((key) => {
      if (!canRenderCard(key)) return;
      const isWide = key === 'lowStock' || key === 'revenue';
      const entry = { key, isWide };
      if (isWide) {
        if (currentRow.length) {
          rows.push(currentRow);
          currentRow = [];
        }
        rows.push([entry]);
      } else {
        currentRow.push(entry);
        if (currentRow.length === 2) {
          rows.push(currentRow);
          currentRow = [];
        }
      }
    });
    if (currentRow.length) rows.push(currentRow);
    return rows;
  }, [cardOrder, canRenderCard]);

  const layoutSignature = useMemo(() => {
    if (!arrangedRows.length) return 'empty';
    return arrangedRows
      .map((row) => (row.length ? row.map(({ key }) => key).join('-') : 'blank'))
      .join('|');
  }, [arrangedRows]);

  const renderCardContent = useCallback(
    (key, refCallback) => {
      const refAttr = refCallback || null;
      if (!canRenderCard(key)) return null;
      switch (key) {
        case 'lowStock':
          return (
            <div className="dashboard-card low-stock-card" ref={refAttr}>
              <div className="low-stock-header">
                <h2>Low Inventory</h2>
                <span className="low-stock-note" style={{ fontSize: '13px' }}>
                  Alerts show when on-hand ≤ SKU threshold
                </span>
              </div>
              <div className={`fade-wrapper ${loadingLowStock ? 'fade-out' : 'fade-in'}`}>
                {lowStock.length > 0 ? (
                  <table className="low-stock-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Qty</th>
                        <th>Threshold</th>
                        <th>Client</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lowStock.map((item) => {
                        const imageSrc = getItemImageSrc(item);
                        const hasImage = Boolean(imageSrc);
                        const skuLabel = item.sku || '(Unknown SKU)';
                        const thumbSrc = hasImage ? imageSrc : NO_IMAGE_PLACEHOLDER;
                        const thumbAlt = hasImage ? `${skuLabel} thumbnail` : `${skuLabel} has no image assigned`;
                        return (
                          <tr key={`${item.clientId || 'c'}-${item.sku}`}>
                            <td>
                              <div className="sku-cell">
                                <div className={`sku-thumb ${hasImage ? '' : 'placeholder'}`}>
                                  <img src={thumbSrc} alt={thumbAlt} loading="lazy" decoding="async" />
                                </div>
                                <div className="sku-text">
                                  <span className="sku-value">{skuLabel}</span>
                                  {item.description && <span className="sku-description">{item.description}</span>}
                                </div>
                              </div>
                            </td>
                            <td>{Number(item?.qtyUnitsOnHand ?? 0).toLocaleString()}</td>
                            <td>{formatLowThreshold(item?.lowThreshold)}</td>
                            <td>{item.clientName || 'Unknown Client'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="loading">{loadingLowStock ? 'Loading…' : 'No SKUs at or below their thresholds.'}</div>
                )}
              </div>
            </div>
          );
        case 'unshippedOrders':
          return (
            <div className="dashboard-card" ref={refAttr}>
              <h2>Unshipped Orders</h2>
              <div className={`fade-wrapper ${loadingOrders ? 'fade-out' : 'fade-in'}`}>
                <table className="orders-table">
                  <thead>
                    <tr>
                      {renderSortHeader('Order Number', 'orderNumber', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                      {renderSortHeader('Created Date', 'createdDate', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                      {renderSortHeader('Client Name', 'clientName', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                      {renderSortHeader('Status', 'statusName', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length > 0 ? (
                      orders.map((order) => {
                        const createdParts = splitDateTime(order.createdDate);
                        return (
                          <tr key={order.orderNumber}>
                            <td>{order.orderNumber}</td>
                            <td className="col-datetime">
                              {createdParts ? (
                                <>
                                  <span>{createdParts.date}</span>
                                  <br />
                                  <span className="col-datetime-time">{createdParts.time}</span>
                                </>
                              ) : (
                                ''
                              )}
                            </td>
                            <td>{order.clientName}</td>
                            <td>{order.statusName}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4">{loadingOrders ? 'Loading…' : 'No unshipped orders found.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        case 'unshippedEOrders':
          return (
            <div className="dashboard-card" ref={refAttr}>
              <h2>Unshipped E-Orders</h2>
              <div className={`fade-wrapper ${loadingNewEOrders ? 'fade-out' : 'fade-in'}`}>
                <table className="eorders-table">
                  <thead>
                    <tr>
                      {renderSortHeader('E-Order #', 'ordernumber', sortFieldEOrders, sortOrderEOrders, handleSortEOrders, 'col-order-number')}
                      {renderSortHeader('Order Date', 'orderdate', sortFieldEOrders, sortOrderEOrders, handleSortEOrders, 'col-order-date')}
                      {renderSortHeader('Status', 'ourstatusname', sortFieldEOrders, sortOrderEOrders, handleSortEOrders)}
                      {renderSortHeader('Client', 'clientname', sortFieldEOrders, sortOrderEOrders, handleSortEOrders, 'col-client-name')}
                      {renderSortHeader('Assigned', 'assignedname', sortFieldEOrders, sortOrderEOrders, handleSortEOrders)}
                    </tr>
                  </thead>
                  <tbody>
                    {newEOrders.length > 0 ? (
                      newEOrders.map((order) => {
                        const created = order.orderdate || order.datecreated;
                        const createdLabel = splitDateTime(created);
                        const orderNumber = order.ordernumber || `#${order.id}`;
                        return (
                          <tr key={order.id || order.ordernumber}>
                            <td className="col-order-number">{orderNumber}</td>
                            <td className="col-order-date col-datetime">
                              {createdLabel ? (
                                <>
                                  <span>{createdLabel.date}</span>
                                  <br />
                                  <span className="col-datetime-time">{createdLabel.time}</span>
                                </>
                              ) : (
                                ''
                              )}
                            </td>
                            <td>{order.ourstatusname || '—'}</td>
                            <td className="col-client-name">{order.clientname || '—'}</td>
                            <td>{order.assignedname || 'Unassigned'}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5">{loadingNewEOrders ? 'Loading…' : 'No pending e-orders found.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        case 'unpaidInvoices':
          return (
            <div className="dashboard-card" ref={refAttr}>
              <h2>Unpaid Invoices</h2>
              <div className={`fade-wrapper ${loadingInvoices ? 'fade-out' : 'fade-in'}`}>
                <table className="invoices-table">
                  <thead>
                    <tr>
                      {renderSortHeader('Invoice ID', 'invoiceId', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                      {renderSortHeader('Invoice Date', 'invoiceDate', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                      {renderSortHeader('Client Name', 'clientName', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                      {renderSortHeader('Amount Due', 'amountDue', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length > 0 ? (
                      invoices.map((inv) => {
                        const invoiceDateParts = splitDateTime(inv.invoiceDate);
                        return (
                          <tr key={inv.invoiceId}>
                            <td>{inv.invoiceId}</td>
                            <td className="col-datetime">
                              {invoiceDateParts ? (
                                <>
                                  <span>{invoiceDateParts.date}</span>
                                  <br />
                                  <span className="col-datetime-time">{invoiceDateParts.time}</span>
                                </>
                              ) : (
                                ''
                              )}
                            </td>
                            <td>{inv.clientName}</td>
                            <td>${formatCurrency(inv.amountDue ?? 0)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4">{loadingInvoices ? 'Loading…' : 'No unpaid invoices found.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        case 'revenue':
          return (
            <div className="dashboard-card dashboard-card-wide" ref={refAttr}>
              <h2>Revenue (Last 6 Months)</h2>
              <div className={`fade-wrapper revenue-wrap ${loadingRevenue ? 'fade-out' : 'fade-in'}`}>
                {revenue.length ? (
                  <div className="revenue-chart" role="img" aria-label="Revenue by month for the last 6 months">
                    {revenue.map((entry, index) => {
                      const total = Number(entry.total) || 0;
                      const fillPct = revenueMax > 0 ? Math.max((total / revenueMax) * 100, total > 0 ? 8 : 0) : 0;
                      return (
                        <div className="revenue-chart__group" key={entry.key || entry.label}>
                          <div className="revenue-chart__value">${formatCurrency(total)}</div>
                          <div className="revenue-chart__track">
                            <div
                              className="revenue-chart__fill"
                              style={{
                                height: `${Math.min(fillPct, 100)}%`,
                                backgroundColor: barColors[index % barColors.length],
                              }}
                              title={`${entry.label}: $${formatCurrency(total)}`}
                            />
                          </div>
                          <div className="revenue-chart__label">{entry.label}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="loading">No revenue data.</div>
                )}
              </div>
            </div>
          );
        default:
          return null;
      }
    },
    [
      canRenderCard,
      loadingLowStock,
      lowStock,
      loadingOrders,
      orders,
      sortFieldOrders,
      sortOrderOrders,
      loadingNewEOrders,
      newEOrders,
      sortFieldEOrders,
      sortOrderEOrders,
      loadingInvoices,
      invoices,
      sortFieldInvoices,
      sortOrderInvoices,
      loadingRevenue,
      revenue,
      revenueMax,
      handleSortOrders,
      handleSortEOrders,
      handleSortInvoices,
      splitElementStyle,
      splitGutterStyle,
    ]
  );

  const handleDragStart = (key) => (event) => {
    setDraggingCard(key);
    try {
      event.dataTransfer.setData('text/plain', key);
      event.dataTransfer.effectAllowed = 'move';
    } catch {}
  };

  const handleDragOver = (key) => (event) => {
    if (!draggingCard || draggingCard === key) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (key) => (event) => {
    event.preventDefault();
    if (!draggingCard || draggingCard === key) return;
    reorderCards(draggingCard, key);
    setDraggingCard(null);
  };

  const handleDragEnd = () => {
    setDraggingCard(null);
  };

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-toolbar">
        <button
          className="btn"
          type="button"
          onClick={() => setShowConfig((v) => !v)}
        >
          Dashboard Config
        </button>
        <button
          className="btn"
          type="button"
          onClick={() => {
            try {
              localStorage.removeItem(DASHBOARD_ORDER_KEY);
            } catch {}
            setCardOrder(DEFAULT_CARD_ORDER);
            Object.values(cardRefs.current).forEach((node) => {
              if (node) {
                node.style.width = '';
                node.style.height = '';
              }
            });
            Object.values(wrapperRefs.current).forEach((node) => {
              if (node) {
                node.style.width = '';
                node.style.height = '';
                node.style.flexBasis = '';
              }
            });
            Object.values(rowRefs.current).forEach((node) => {
              if (node) {
                node.style.height = '';
                node.style.flexBasis = '';
                node.style.width = '';
              }
            });
            setSplitVersion((v) => v + 1);
          }}
          style={{ marginLeft: '0.5rem' }}
        >
          Reset Card Layout
        </button>
      </div>
      {showConfig && (
        <div className="dashboard-config">
          <h4>Choose which cards to display</h4>
          <div className="dashboard-config-grid">
            {DASHBOARD_CARD_KEYS.map((key) => {
              if (IS_CLIENT && key === 'revenue') return null;
              if (key === 'unpaidInvoices' && !canSeeUnpaidInvoices) return null;
              return (
              <label key={key} className="config-row">
                <input
                  type="checkbox"
                  checked={visibleCards[key]}
                  onChange={() => toggleCardVisibility(key)}
                />
                <span>
                  {(() => {
                    switch (key) {
                      case 'lowStock':
                        return 'Low Inventory';
                      case 'unshippedOrders':
                        return 'Unshipped Orders';
                      case 'unshippedEOrders':
                        return 'Unshipped E-Orders';
                      case 'unpaidInvoices':
                        return 'Unpaid Invoices';
                      case 'revenue':
                        return 'Revenue';
                      default:
                        return key;
                    }
                  })()}
                </span>
              </label>
              );
            })}
          </div>
        </div>
      )}
      <div className="dashboard-content">
        {arrangedRows.length ? (
          arrangedRows.length > 1 ? (
            <Split
              key={`vertical-${splitVersion}-${layoutSignature}`}
              direction="vertical"
              className="dashboard-vertical-split"
              sizes={arrangedRows.map(() => 100 / arrangedRows.length)}
              minSize={220}
              gutterSize={10}
              elementStyle={splitElementStyle}
              gutterStyle={splitGutterStyle}
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              {arrangedRows.map((rowCards, rowIdx) => {
                const rowSignature = rowCards.length
                  ? rowCards.map(({ key }) => key).join('-')
                  : `row-${rowIdx}`;
                return (
                  <div
                    key={`row-pane-${splitVersion}-${rowIdx}-${rowSignature}`}
                    className="dashboard-row-pane"
                    ref={(node) => registerRowRef(`row-${rowIdx}`, node)}
                  >
                    {rowCards.length > 1 ? (
                      <Split
                        key={`row-${rowIdx}-h-${splitVersion}-${rowSignature}`}
                        direction="horizontal"
                        className="dashboard-row-split"
                        sizes={rowCards.map(() => 100 / rowCards.length)}
                        minSize={260}
                        gutterSize={10}
                        elementStyle={splitElementStyle}
                        gutterStyle={splitGutterStyle}
                        style={{ width: '100%', height: '100%', minHeight: 0 }}
                      >
                        {rowCards.map(({ key, isWide }) => {
                          const refCb = (node) => registerCardRef(key, node);
                          const content = renderCardContent(key, refCb);
                          if (!content) return null;
                          return (
                            <div
                              key={key}
                              className={`dashboard-card-wrapper ${isWide ? 'span-2' : ''} ${draggingCard === key ? 'is-dragging' : ''}`}
                              draggable
                              onDragStart={handleDragStart(key)}
                              onDragEnd={handleDragEnd}
                              onDragOver={handleDragOver(key)}
                              onDrop={handleDrop(key)}
                              ref={(node) => registerWrapperRef(key, node)}
                            >
                              {content}
                            </div>
                          );
                        })}
                      </Split>
                    ) : (
                      <div className="dashboard-row-single">
                        {rowCards.map(({ key, isWide }) => {
                          const refCb = (node) => registerCardRef(key, node);
                          const content = renderCardContent(key, refCb);
                          if (!content) return null;
                          return (
                            <div
                              key={key}
                              className={`dashboard-card-wrapper ${isWide ? 'span-2' : ''} ${draggingCard === key ? 'is-dragging' : ''}`}
                              draggable
                              onDragStart={handleDragStart(key)}
                              onDragEnd={handleDragEnd}
                              onDragOver={handleDragOver(key)}
                              onDrop={handleDrop(key)}
                              ref={(node) => registerWrapperRef(key, node)}
                            >
                              {content}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </Split>
          ) : (
            arrangedRows.map((rowCards, rowIdx) => {
              const rowSignature = rowCards.length
                ? rowCards.map(({ key }) => key).join('-')
                : `row-${rowIdx}`;
              return (
                <div
                  key={`row-pane-${splitVersion}-${rowIdx}-${rowSignature}`}
                  className="dashboard-row-pane single"
                  ref={(node) => registerRowRef(`row-${rowIdx}`, node)}
                >
                  {rowCards.length > 1 ? (
                    <Split
                      key={`row-single-${rowIdx}-h-${splitVersion}-${rowSignature}`}
                      direction="horizontal"
                      className="dashboard-row-split"
                      sizes={rowCards.map(() => 100 / rowCards.length)}
                      minSize={260}
                      gutterSize={10}
                      elementStyle={splitElementStyle}
                      gutterStyle={splitGutterStyle}
                      style={{ width: '100%', height: '100%', minHeight: 0 }}
                    >
                      {rowCards.map(({ key, isWide }) => {
                        const refCb = (node) => registerCardRef(key, node);
                        const content = renderCardContent(key, refCb);
                        if (!content) return null;
                        return (
                          <div
                            key={key}
                            className={`dashboard-card-wrapper ${isWide ? 'span-2' : ''} ${draggingCard === key ? 'is-dragging' : ''}`}
                            draggable
                            onDragStart={handleDragStart(key)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver(key)}
                            onDrop={handleDrop(key)}
                            ref={(node) => registerWrapperRef(key, node)}
                          >
                            {content}
                          </div>
                        );
                      })}
                    </Split>
                  ) : (
                    <div className="dashboard-row-single">
                      {rowCards.map(({ key, isWide }) => {
                        const refCb = (node) => registerCardRef(key, node);
                        const content = renderCardContent(key, refCb);
                        if (!content) return null;
                        return (
                          <div
                            key={key}
                            className={`dashboard-card-wrapper ${isWide ? 'span-2' : ''} ${draggingCard === key ? 'is-dragging' : ''}`}
                            draggable
                            onDragStart={handleDragStart(key)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver(key)}
                            onDrop={handleDrop(key)}
                            ref={(node) => registerWrapperRef(key, node)}
                          >
                            {content}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )
        ) : (
          <div className="dashboard-empty-state">No cards enabled. Use Dashboard Config to enable cards.</div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
